import {Color} from '@benchling/nucleus-ui';
import {StyleSheet, css} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import {BORDER_RADIUS_DEFAULT} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
// Type-only import (erased at build time, so no runtime import cycle with EditableTextField) used to
// let `autoSize` be typed as textarea-only even when callers pass the TextFieldType enum.
import type {TextFieldType} from 'ui-library/inputs/EditableTextField';
import {ElementMouseEvent} from 'util/Types';

export type FormInputType = string | 'text' | 'number' | 'textarea' | 'checkbox';

/**
 * Indicate where in an input group the FormInput appears to control rounding:
 *
 * 'left': results in square corners on the right side of the input.
 *
 *          ╭-----------+---╮
 *          | FormInput | X |
 *          ╰-----------+---╯
 *
 * 'right': results in square corners on the left side of the input.
 *
 *      ╭---+-----------╮
 *      | X | FormInput |
 *      ╰---+-----------╯
 *
 * 'middle': results in square corners on both sides of the input.
 *
 *      ╭---+-----------+---╮
 *      | X | FormInput | X |
 *      ╰---+-----------+---╯
 *
 * undefined (default): results in rounded corners on both sides of the input.
 *
 *          ╭-----------╮
 *          | FormInput |
 *          ╰-----------╯
 */
export type InputGroupPosition = 'left' | 'middle' | 'right';

// Opt-in class that enables content-based auto-sizing for textareas (see `textarea.textarea-autosize`
// in less/almostflat.less). Applied when the `autoSize` prop is set.
const TEXTAREA_AUTOSIZE_CLASS = 'textarea-autosize';

interface BaseFormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  className?: string;
  error?: string;
  focusOnMount: boolean;
  selectOnMount?: boolean;
  id?: string;
  name?: string;
  onClick?: (e: ElementMouseEvent) => void;
  placeholder?: string;
  selectAllOnClick: boolean;
  dataTestAttrs?: DataTestAttrs;
  rows?: number;
  maxLength?: number;
  inputGroupPosition?: InputGroupPosition;
  disabled?: boolean;
}

// `autoSize` (content-based vertical growth) only makes sense for a textarea, so it's typed to be
// available only when `type` is statically a textarea (the `'textarea'` literal or the equivalent
// `TextFieldType.TEXTAREA` enum member that higher-level components pass through). Other input types
// cannot set `autoSize`.
interface TextareaFormInputProps extends BaseFormInputProps {
  type: 'textarea' | TextFieldType.TEXTAREA;
  /**
   * Opt in to content-based auto-sizing: the textarea grows with its content between a ~3-row
   * minimum and ~10-row maximum instead of opening one line tall.
   */
  autoSize?: boolean;
}

interface NonTextareaFormInputProps extends BaseFormInputProps {
  type?: FormInputType;
  autoSize?: never;
}

type FormInputProps = TextareaFormInputProps | NonTextareaFormInputProps;

class FormInput extends React.Component<FormInputProps> {
  static defaultProps = {
    type: 'text',
    focusOnMount: false,
    selectAllOnClick: false,
  };

  inputRef = React.createRef<HTMLInputElement | HTMLTextAreaElement>();

  override componentDidMount(): void {
    if (this.props.focusOnMount) {
      this.inputRef.current?.focus();
    }
    if (this.props.selectOnMount) {
      this.inputRef.current?.select();
    }
  }

  // Deprecated: use the focusOnMount property instead
  focus(): void {
    this.inputRef.current!.select();
  }

  _handleClick(e: ElementMouseEvent): void {
    if (this.props.selectAllOnClick) {
      this.inputRef.current!.select();
    }
    if (this.props.onClick) {
      this.props.onClick(e);
    }
  }

  getInputGroupStyle(): object | null {
    switch (this.props.inputGroupPosition) {
      case 'left':
        return styles.inputGroupLeft;
      case 'right':
        return styles.inputGroupRight;
      case 'middle':
        return styles.inputGroupMiddle;
      default:
        return null;
    }
  }

  override render(): JSX.Element {
    const sharedProps = {
      // eslint-disable-next-line no-restricted-syntax
      ..._.omit(
        this.props,
        'error',
        'focusOnMount',
        'selectOnMount',
        'selectAllOnClick',
        'dataTestAttrs',
        'rows',
        'type',
        'inputGroupPosition',
        'autoSize'
      ),
      ref: this.inputRef,
      className: classNames(
        this.props.className,
        'form-control',
        css(styles.input, this.getInputGroupStyle()),
        this.props.disabled && css(styles.disabled),
        this.props.autoSize && this.props.type === 'textarea' && TEXTAREA_AUTOSIZE_CLASS,
        {
          'fieldInput-input--error': Boolean(this.props.error),
        }
      ),
      onClick: (e: ElementMouseEvent) => {
        this._handleClick(e);
      },
      ...getTestProps(this.props.dataTestAttrs),
    };

    return this.props.type === 'textarea' ? (
      <textarea {...sharedProps} rows={this.props.rows} aria-invalid={Boolean(this.props.error)} />
    ) : (
      <input {...sharedProps} type={this.props.type} aria-invalid={Boolean(this.props.error)} />
    );
  }
}

export default FormInput;

const styles = StyleSheet.create({
  disabled: {
    backgroundColor: Color.GRAY_100,
    borderColor: Color.GRAY_100,
    // Set on color and placeholder color so the text is the right color for placeholders and values
    color: Color.GRAY_600,
    '::placeholder': {
      color: Color.GRAY_600,
    },
  },

  input: {
    borderRadius: BORDER_RADIUS_DEFAULT,
  },
  inputGroupLeft: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  inputGroupRight: {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  inputGroupMiddle: {
    borderRadius: 0,
  },
});
